// Inventory counts — the weekly walk-through, as its own place (like the
// logbook). Count by camera or by hand; items with container standards are
// counted in the containers the shelf actually holds (cases, bins, cups,
// trays) and the sheet does the pounds math in front of you.
import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Camera, ClipboardList, Moon } from 'lucide-react';
import api from '../services/api';

// Offline safety: the sheet drafts to this device as you type, and counts
// applied without a connection queue here until the network is back.
const DRAFT_KEY = 'mise-count-draft';
const QUEUE_KEY = 'mise-count-queue';

interface PhotoRow {
  itemId: string; name: string; unit: string;
  loose: number;                       // stock units counted directly (or the camera's estimate)
  conts: Record<string, number>;       // containers counted, by label
  basis: string | null; confidence: string;
  expected: number; pendingDelivery: number;
}

const round2 = (n: number) => Math.round(n * 100) / 100;

export default function CountsPage() {
  const queryClient = useQueryClient();
  const [photoRows, setPhotoRows] = useState<PhotoRow[]>([]);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [photoNote, setPhotoNote] = useState('');
  const [applying, setApplying] = useState(false);
  const [unknownSeen, setUnknownSeen] = useState<Array<{ name: string; estimate: string }>>([]);
  const [recap, setRecap] = useState('');
  const [addPick, setAddPick] = useState('');

  // bagel nightly count
  const today = new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
  const [bagels, setBagels] = useState<Record<string, { delivered: string; counted: string }>>({
    everything: { delivered: '', counted: '' }, sesame: { delivered: '', counted: '' }, plain: { delivered: '', counted: '' },
  });
  const [bagelNote, setBagelNote] = useState('');

  const { data: inventoryList } = useQuery({
    queryKey: ['inventory'],
    queryFn: async () => (await api.get('/v1/inventory/')).data?.data || [],
  });
  const { data: bagelLog } = useQuery({
    queryKey: ['bagel-log'],
    queryFn: async () => (await api.get('/v1/inventory/bagel-log')).data?.data || { entries: [] },
  });
  const { data: bagelPlan } = useQuery({
    queryKey: ['bagel-plan'],
    queryFn: async () => (await api.get('/v1/inventory/bagel-plan')).data?.data,
  });

  // restore an unfinished sheet from this device; keep it saved as you type
  useEffect(() => {
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (raw) {
        const draft = JSON.parse(raw);
        if (Array.isArray(draft) && draft.length > 0) {
          setPhotoRows(draft);
          setPhotoNote('Picked up your unfinished count from this device.');
        }
      }
    } catch { /* a bad draft never blocks counting */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    try {
      if (photoRows.length > 0) localStorage.setItem(DRAFT_KEY, JSON.stringify(photoRows));
      else localStorage.removeItem(DRAFT_KEY);
    } catch { /* storage full — nothing to do */ }
  }, [photoRows]);

  // flush counts applied while offline, on load and whenever the network returns
  const flushQueue = async () => {
    let queue: any[] = [];
    try { queue = JSON.parse(localStorage.getItem(QUEUE_KEY) || '[]'); } catch { queue = []; }
    if (!queue.length) return;
    const still: any[] = [];
    let synced = 0;
    for (const q of queue) {
      try { await api.post(q.url, q.body); synced++; }
      catch (e: any) {
        if (e?.response) synced++; // server saw it and said no — don't retry forever
        else still.push(q);        // network — keep for next time
      }
    }
    localStorage.setItem(QUEUE_KEY, JSON.stringify(still));
    if (synced > 0) {
      setPhotoNote(`Synced ${synced} count${synced === 1 ? '' : 's'} made offline.`);
      queryClient.invalidateQueries({ queryKey: ['count-log'] });
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
    }
  };
  useEffect(() => {
    flushQueue();
    window.addEventListener('online', flushQueue);
    return () => window.removeEventListener('online', flushQueue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { data: log } = useQuery({
    queryKey: ['count-log'],
    queryFn: async () => (await api.get('/v1/inventory/count-log')).data?.data || [],
  });
  const { data: containersMap } = useQuery({
    queryKey: ['count-containers'],
    queryFn: async () => (await api.get('/v1/inventory/count-containers')).data?.data || {},
  });

  const totalFor = (r: PhotoRow) => {
    const defs: Array<{ label: string; qty: number }> = (containersMap || {})[r.itemId] || [];
    return round2(r.loose + defs.reduce((s, c) => s + (r.conts[c.label] || 0) * c.qty, 0));
  };

  const handlePhoto = async (file: File) => {
    setPhotoBusy(true); setPhotoNote('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/v1/inventory/photo-count', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const d = res.data?.data || {};
      const rows: any[] = d.rows || [];
      setPhotoRows((prev) => {
        const byId = new Map(prev.map((r) => [r.itemId, r]));
        for (const r of rows) {
          const ex = byId.get(r.itemId);
          // same item in a second photo: keep the LARGER count, never sum —
          // two angles of one shelf must not double-count. If these really
          // were different shelves, bump the number by hand.
          if (ex) byId.set(r.itemId, { ...ex, loose: round2(Math.max(ex.loose, r.counted)), basis: [ex.basis, `also seen: ${r.basis}`].filter(Boolean).join(' · ') });
          else byId.set(r.itemId, { itemId: r.itemId, name: r.name, unit: r.unit, loose: r.counted, conts: {}, basis: r.basis, confidence: r.confidence, expected: r.expected, pendingDelivery: r.pendingDelivery });
        }
        return [...byId.values()];
      });
      setPhotoNote(rows.length ? `${rows.length} item${rows.length === 1 ? '' : 's'} read — the camera counts what it can SEE; back rows it can't see behind stay yours to confirm. Same item in two photos keeps the larger count.` : (d.sceneNotes || 'Nothing countable recognized — try a closer shot.'));
      if (Array.isArray(d.unknownItems) && d.unknownItems.length > 0) {
        setUnknownSeen((prev) => {
          const names = new Set(prev.map((x) => x.name.toLowerCase()));
          return [...prev, ...d.unknownItems.filter((u: any) => !names.has(String(u.name).toLowerCase()))];
        });
      }
    } catch (e: any) {
      setPhotoNote(e?.response?.data?.error?.message || 'The photo could not be read.');
    } finally { setPhotoBusy(false); }
  };

  const applyCounts = async () => {
    setApplying(true);
    // recap computed up front so it survives the sheet clearing
    const within = photoRows.filter((r) => r.expected > 0 && Math.abs(totalFor(r) - r.expected) / r.expected <= 0.1).length;
    const biggest = [...photoRows].sort((a, b) => Math.abs(totalFor(b) - b.expected) - Math.abs(totalFor(a) - a.expected))[0];
    let wentOffline = false;
    const failed: string[] = [];
    try {
      for (const r of photoRows) {
        const total = totalFor(r);
        const delta = Math.round((total - r.expected) * 1000) / 1000;
        if (Math.abs(delta) < 0.01) continue;
        const defs: Array<{ label: string; qty: number }> = (containersMap || {})[r.itemId] || [];
        const parts = defs.filter((c) => (r.conts[c.label] || 0) > 0).map((c) => `${r.conts[c.label]} × ${c.label}`);
        if (r.loose > 0) parts.push(`${r.loose} loose ${r.unit}`);
        const url = `/v1/inventory/${r.itemId}/transactions`;
        const body = {
          type: 'adjustment', quantity: delta, reason: 'Inventory count',
          notes: `Photo count: ${total} ${r.unit}${parts.length ? ` (${parts.join(' + ')})` : ''}`,
        };
        try {
          await api.post(url, body);
        } catch (e: any) {
          if (e?.response) { failed.push(r.name); continue; } // server said no — keep going, report at the end
          // no network: queue it and keep going
          wentOffline = true;
          const queue = JSON.parse(localStorage.getItem(QUEUE_KEY) || '[]');
          queue.push({ url, body });
          localStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
        }
      }
      const gapLine = biggest && Math.abs(totalFor(biggest) - biggest.expected) >= 0.01
        ? ` Biggest gap: ${biggest.name} (${Math.round((totalFor(biggest) - biggest.expected) * 10) / 10} ${biggest.unit}).`
        : '';
      setRecap(wentOffline
        ? `Counted ${photoRows.length} items — saved on this device, they'll sync the moment you're back online.`
        : `Counted ${photoRows.length} items; ${within} matched the system within 10%.${gapLine}${failed.length ? ` ${failed.length} didn't save (${failed.join(', ')}) — count those again.` : ''}`);
      setPhotoRows([]); setPhotoNote('');
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      queryClient.invalidateQueries({ queryKey: ['count-log'] });
    } finally { setApplying(false); }
  };

  const addItemToSheet = (itemId: string) => {
    const it = (inventoryList || []).find((x: any) => x.id === itemId);
    if (!it || photoRows.some((r) => r.itemId === itemId)) return;
    setPhotoRows((prev) => [...prev, {
      itemId: it.id, name: it.name, unit: it.unitOfMeasure || it.unit || '',
      loose: 0, conts: {}, basis: null, confidence: 'manual',
      expected: parseFloat(String(it.currentQuantity)) || 0,
      pendingDelivery: Number(it.onOrderQuantity) || 0,
    }]);
  };

  const submitBagels = async () => {
    try {
      const payload: any = { date: today, delivered: {}, counted: {} };
      for (const t of ['everything', 'sesame', 'plain']) {
        payload.delivered[t] = parseFloat(bagels[t].delivered) || 0;
        payload.counted[t] = parseFloat(bagels[t].counted) || 0;
      }
      const res = await api.post('/v1/inventory/bagel-log', payload);
      const d = res.data?.data;
      setBagelNote(!d?.soldKnown
        ? 'Logged — tonight\'s sales land with the nightly sync, waste fills in then.'
        : d.typed
          ? `Logged. Waste tonight: ${['everything', 'sesame', 'plain'].map((t) => `${d.waste[t]} ${t}`).join(', ')}.`
          : `Logged. ${d.totals.delivered} in, ${d.totals.sold} sold, ${d.totals.counted} left → ${d.totals.waste} unaccounted for tonight.`);
      queryClient.invalidateQueries({ queryKey: ['bagel-log'] });
    } catch (e: any) {
      setBagelNote(e?.response?.data?.error?.message || 'Could not log — try again in a moment.');
    }
  };

  // Track record: how close the system's picture is to the shelf, count after
  // count — grouped per item, newest first; the headline is the learning curve.
  const track = (() => {
    const byItem = new Map<string, any[]>();
    for (const e of (log || []) as any[]) {
      if (!byItem.has(e.itemName)) byItem.set(e.itemName, []);
      byItem.get(e.itemName)!.push(e);
    }
    const rows = [...byItem.entries()].map(([name, es]) => {
      const sorted = [...es].sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime());
      const pcts = sorted.map((e) => (e.believed > 0 ? ((e.counted - e.believed) / e.believed) * 100 : 0));
      return { name, unit: sorted[0]?.unit || '', n: sorted.length, pcts, latest: pcts[pcts.length - 1], first: pcts[0] };
    }).filter((r) => r.n >= 1).sort((a, b) => b.n - a.n || Math.abs(b.latest) - Math.abs(a.latest));
    const multi = rows.filter((r) => r.n >= 2);
    const avgFirst = multi.length ? multi.reduce((s, r) => s + Math.abs(r.first), 0) / multi.length : null;
    const avgLatest = multi.length ? multi.reduce((s, r) => s + Math.abs(r.latest), 0) / multi.length : null;
    return { rows, avgFirst, avgLatest };
  })();

  // group log by day, logbook-style
  const groups: Array<{ day: string; rows: any[] }> = [];
  for (const e of (log || []) as any[]) {
    const day = new Date(e.at).toLocaleDateString('en-US', { timeZone: 'America/New_York', weekday: 'long', month: 'long', day: 'numeric' });
    const g = groups.find((x) => x.day === day);
    if (g) g.rows.push(e); else groups.push({ day, rows: [e] });
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Counts</h1>
        <p className="text-slate-500">Count what you see — cases, bins, cups, trays — and the sheet does the math. Every count teaches the system your kitchen.</p>
      </div>

      {recap && (
        <div className="card p-4 bg-emerald-50/60 border-emerald-200 flex items-start justify-between gap-3">
          <p className="text-sm text-emerald-900">{recap}</p>
          <button className="text-emerald-700/60 hover:text-emerald-900 text-sm" onClick={() => setRecap('')}>✕</button>
        </div>
      )}
      {unknownSeen.length > 0 && (
        <div className="card p-4 bg-amber-50/60 border-amber-200">
          <p className="text-sm text-amber-900">
            The camera also saw {unknownSeen.map((u) => u.name).join(', ')} — not on this store's item list, so {unknownSeen.length === 1 ? "it's" : "they're"} flagged for a manager to review in Inventory. Counts never add items on their own.
          </p>
        </div>
      )}

      {/* new count */}
      <div className="card p-5 space-y-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-primary-50 flex items-center justify-center shrink-0"><Camera className="w-5 h-5 text-primary-600" /></div>
            <div>
              <p className="text-sm font-medium text-slate-900">Count by camera</p>
              <p className="text-xs text-slate-500">{photoBusy ? 'Reading the photo…' : photoNote || 'Photograph each shelf — estimates prefill the sheet, you confirm in containers or loose units.'}</p>
            </div>
          </div>
          <label className={`text-sm px-3 py-2 rounded-lg font-medium cursor-pointer ${photoBusy ? 'bg-slate-200 text-slate-400' : 'bg-primary-600 text-white'}`}>
            {photoBusy ? 'Reading…' : photoRows.length ? 'Add photo' : 'Take or choose photo'}
            <input type="file" accept="image/*,.heic,.heif" className="hidden" disabled={photoBusy}
              onChange={(e) => { const f = e.target.files?.[0]; if (f) handlePhoto(f); e.target.value = ''; }} />
          </label>
        </div>

        <div className="flex items-center gap-2">
          <select className="input input-sm max-w-xs" value={addPick}
            onChange={(e) => { if (e.target.value) { addItemToSheet(e.target.value); setAddPick(''); } }}>
            <option value="">Count an item by hand…</option>
            {((inventoryList || []) as any[])
              .filter((i: any) => !photoRows.some((r) => r.itemId === i.id))
              .sort((a: any, b: any) => a.name.localeCompare(b.name))
              .map((i: any) => <option key={i.id} value={i.id}>{i.name}</option>)}
          </select>
          <span className="text-xs text-slate-400">no photo needed — pick items and count them straight into the sheet</span>
        </div>

        {photoRows.length > 0 && (
          <>
            <div className="divide-y divide-slate-100">
              {photoRows.map((r) => {
                const defs: Array<{ label: string; qty: number }> = (containersMap || {})[r.itemId] || [];
                const total = totalFor(r);
                const diff = round2(total - r.expected);
                return (
                  <div key={r.itemId} className="py-3 grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                      <p className="font-medium text-slate-900">{r.name}</p>
                      <p className="text-xs text-slate-400 mt-0.5">{r.basis || ''}{r.confidence === 'low' ? ' · low confidence' : ''}</p>
                      {r.pendingDelivery > 0 && <p className="text-[11px] text-amber-600 mt-0.5">a delivery with {r.pendingDelivery} {r.unit} is still on the road</p>}
                    </div>
                    <div className="space-y-1">
                      {defs.map((c) => (
                        <div key={c.label} className="flex items-center justify-between gap-2">
                          <span className="text-xs text-slate-500">{c.label}</span>
                          <input type="number" min="0" step="1" className="input input-sm w-20 text-right" placeholder="0"
                            value={r.conts[c.label] ?? ''}
                            onChange={(e) => setPhotoRows((prev) => prev.map((x) => x.itemId === r.itemId ? { ...x, conts: { ...x.conts, [c.label]: parseFloat(e.target.value) || 0 } } : x))} />
                        </div>
                      ))}
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-xs text-slate-500">{defs.length ? `loose ${r.unit}` : `total ${r.unit}`}</span>
                        <input type="number" min="0" step="0.1" className="input input-sm w-20 text-right"
                          value={r.loose}
                          onChange={(e) => setPhotoRows((prev) => prev.map((x) => x.itemId === r.itemId ? { ...x, loose: parseFloat(e.target.value) || 0 } : x))} />
                      </div>
                    </div>
                    <div className="text-sm md:text-right space-y-0.5">
                      <p className="font-semibold text-slate-900 tabular-nums">You counted {total} {r.unit}</p>
                      <p className="text-slate-500 tabular-nums">System thinks {r.expected} {r.unit}</p>
                      <p className={`tabular-nums font-medium ${Math.abs(diff) < 0.01 ? 'text-slate-400' : diff < 0 ? 'text-red-600' : 'text-emerald-700'}`}>
                        {diff === 0 ? 'Matches' : diff < 0 ? `${Math.abs(diff)} ${r.unit} less than expected` : `${diff} ${r.unit} more than expected`}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-slate-400">"System thinks" = on hand from sales depletion since the last count. Less than expected usually means overprep, waste, or an unrecorded transfer — applying the count teaches the system either way.</p>
            <div className="flex items-center justify-between">
              <button onClick={() => { setPhotoRows([]); setPhotoNote(''); }} className="btn btn-sm btn-ghost">Discard</button>
              <button onClick={applyCounts} disabled={applying} className="btn btn-sm btn-primary">{applying ? 'Applying…' : 'Apply counts'}</button>
            </div>
          </>
        )}
      </div>

      {/* nightly bagel count — only where the sales data actually has bagels
          (a juice brand or a bistro never sees this card) */}
      {((bagelPlan?.suggested && (bagelPlan.suggested.everything + bagelPlan.suggested.sesame + bagelPlan.suggested.plain + (bagelPlan.suggested.unsplit || 0) + bagelPlan.suggested.assortedDozens) > 0) || ((bagelLog?.entries || []).length > 0)) && (
      <div className="card p-5 space-y-3">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-indigo-50 flex items-center justify-center shrink-0"><Moon className="w-5 h-5 text-indigo-600" /></div>
          <div className="flex-1">
            <p className="text-sm font-medium text-slate-900">Nightly bagel count</p>
            <p className="text-xs text-slate-500">At close: how many came in from the bakery, how many are left. Sales fill in the middle — what's unaccounted for is the night's waste.</p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {(['everything', 'sesame', 'plain'] as const).map((t) => (
            <div key={t} className="border border-slate-100 rounded-lg p-3 space-y-1.5">
              <p className="text-sm font-medium text-slate-700 capitalize">{t}</p>
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs text-slate-500">delivered</span>
                <input type="number" min="0" className="input input-sm w-20 text-right" placeholder="0"
                  value={bagels[t].delivered} onChange={(e) => setBagels((b) => ({ ...b, [t]: { ...b[t], delivered: e.target.value } }))} />
              </div>
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs text-slate-500">left at close</span>
                <input type="number" min="0" className="input input-sm w-20 text-right" placeholder="0"
                  value={bagels[t].counted} onChange={(e) => setBagels((b) => ({ ...b, [t]: { ...b[t], counted: e.target.value } }))} />
              </div>
            </div>
          ))}
        </div>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <p className="text-xs text-slate-400">{bagelNote || (bagelPlan?.suggested ? `Tomorrow's suggested bake: ${[['everything', bagelPlan.suggested.everything], ['sesame', bagelPlan.suggested.sesame], ['plain', bagelPlan.suggested.plain], ['bagels', bagelPlan.suggested.unsplit || 0], ['assorted dozens', bagelPlan.suggested.assortedDozens]].filter(([, v]) => (v as number) > 0).map(([k, v]) => `${(v as number).toLocaleString('en-US')} ${k}`).join(' · ')} — full order lives in Ordering.` : '')}</p>
          <button className="btn btn-sm btn-primary" onClick={submitBagels}>Log tonight</button>
        </div>
        {((bagelLog?.entries || []) as any[]).length > 0 && (
          <div className="pt-2 border-t border-slate-100 space-y-1">
            {(bagelLog.entries as any[]).slice(0, 7).map((e: any) => (
              <div key={e.date} className="flex items-center justify-between gap-3 text-sm flex-wrap">
                <span className="text-slate-500">{new Date(e.date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</span>
                <span className="tabular-nums text-slate-700">
                  {!e.soldKnown
                    ? 'waiting on that night\'s sales'
                    : e.typed
                      ? `waste ${['everything', 'sesame', 'plain'].map((t) => `${e.waste?.[t] ?? '—'} ${t.slice(0, 1).toUpperCase()}`).join(' · ')}`
                      : `${e.totals?.delivered ?? '—'} in · ${e.totals?.sold ?? '—'} sold · ${e.totals?.counted ?? '—'} left · waste ${e.totals?.waste ?? '—'}`}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
      )}

      {/* track record — believed vs counted, over time */}
      {track.rows.length > 0 && (
        <div className="card p-5">
          <div className="flex items-baseline justify-between gap-3 flex-wrap mb-3">
            <p className="text-xs uppercase tracking-wide text-slate-400">Track record · system vs shelf</p>
            {track.avgFirst !== null && track.avgLatest !== null && (
              <p className="text-sm">
                <span className="text-slate-500">avg gap </span>
                <span className="tabular-nums text-slate-900 font-medium">{Math.round(track.avgFirst)}%</span>
                <span className="text-slate-400"> → </span>
                <span className={`tabular-nums font-semibold ${track.avgLatest <= track.avgFirst ? 'text-emerald-700' : 'text-red-600'}`}>{Math.round(track.avgLatest)}%</span>
                <span className="text-slate-400 text-xs ml-1.5">{track.avgLatest <= track.avgFirst ? 'the system is learning your kitchen' : 'drifting — worth a look'}</span>
              </p>
            )}
          </div>
          <div className="space-y-2">
            {track.rows.slice(0, 12).map((r) => (
              <div key={r.name} className="flex items-center justify-between gap-3 flex-wrap">
                <span className="text-sm text-slate-700">{r.name} <span className="text-xs text-slate-400">{r.n} count{r.n === 1 ? '' : 's'}</span></span>
                <div className="flex items-center gap-1.5">
                  {r.pcts.slice(-5).map((p: number, i: number) => (
                    <span key={i} className={`rounded px-1.5 py-0.5 text-xs font-medium tabular-nums ${Math.abs(p) < 2 ? 'bg-slate-50 text-slate-400' : p < 0 ? 'bg-red-50 text-red-700' : 'bg-emerald-50 text-emerald-700'}`}>
                      {p > 0 ? '+' : ''}{Math.round(p)}%
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-400 mt-3">Each chip is one count: how far the shelf was from what the system believed, oldest → newest. Shrinking chips mean depletion is dialing in.</p>
        </div>
      )}

      {/* the log */}
      {groups.length === 0 ? (
        <div className="card p-6 text-center text-slate-500">
          <ClipboardList className="w-10 h-10 mx-auto mb-2 opacity-30" />
          <p>No counts yet. The first one anchors on-hand; from the second, the system starts learning your real usage.</p>
        </div>
      ) : (
        <div className="space-y-5">
          {groups.map((g) => (
            <div key={g.day}>
              <p className="text-xs uppercase tracking-wide text-slate-400 mb-2">{g.day}</p>
              <div className="card divide-y divide-slate-100">
                {g.rows.map((e: any) => (
                  <div key={e.id} className="px-5 py-2.5 flex items-baseline justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <span className="text-sm font-medium text-slate-900">{e.itemName}</span>
                      <span className="text-xs text-slate-400 ml-2">{e.method === 'camera' ? '📷 camera' : 'by hand'}</span>
                    </div>
                    <div className="text-sm tabular-nums whitespace-nowrap">
                      <span className="text-slate-500">system thought {e.believed}</span>
                      <span className="text-slate-400"> · </span>
                      <span className="font-medium text-slate-900">counted {e.counted} {e.unit}</span>
                      <span className={`ml-2 rounded px-1.5 py-0.5 text-xs font-medium ${Math.abs(e.delta) < 0.01 ? 'bg-slate-50 text-slate-400' : e.delta < 0 ? 'bg-red-50 text-red-700' : 'bg-emerald-50 text-emerald-700'}`}>
                        {e.delta > 0 ? '+' : ''}{round2(e.delta)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
